import { walletTools } from '@halo/wallet-app';
import { matchRoutes, useNavigate } from 'react-router';
import {
  type Client,
  GroupMemberJoinMode,
  GroupStatusEnum,
  useAppStore,
} from '@halo/chat-sdk-react';
import { tools } from '@halo/ui-chat-react';
import Paths from '@/configs/router/paths';
import { type ActionsStore, useActionsStore } from '@/store/actions';
import { router } from '@/scripts/router/router';
import { router as walletRouter } from '@/wallet';
import { i18n } from '@/store/i18n/i18n';

interface HandlerReturn {
  test: (url: URL) => boolean;
  handle: (url: URL) => unknown | PromiseLike<unknown>;
}

async function handleDeepLinkHaloChatDetail({
  client,
  fetchProfileUser,
  fetchProfileGroup,
  url,
}: {
  client?: Client;
  fetchProfileUser: ActionsStore['fetchProfileUser'];
  fetchProfileGroup: ActionsStore['fetchProfileGroup'];
  url: URL;
}) {
  if (!client) return;
  const id = url.pathname.split('/').pop();
  if (!id) return;

  const res = await client.Api.common.getShareData({ u: id });

  if (res.group.gid) {
    // jump to session
    // const { chat_id } = await joinSession(res.group.gid);
    // navigate(`${Paths.MESSAGES_CHATS}/${chat_id}`);
    const _ = await fetchProfileGroup(res.group.gid, GroupMemberJoinMode.Link, true);
    if (_?.status === GroupStatusEnum.Unknown) {
      tools.toaster.error({
        title: i18n.t('tips.groupDisbanded'),
      });
    }
  }

  if (res.user.user_id) {
    // open profile
    const { promise } = fetchProfileUser(res.user.user_id);
    await promise;
  }
}
async function handleDeepLinkHaloWalletReceive({ client, url }: { client?: Client; url: URL }) {
  if (!client) return;
  const id = decodeURIComponent(url.pathname.split('/').pop() ?? '');
  if (!id || !id.includes(':')) return;
  let link: string | undefined = undefined;
  // long link
  if (id.includes(':')) {
    const _ = new URL(id);
    _.searchParams.forEach((value, key) => {
      _.searchParams.set(key, value);
    });
    link = _.toString();
  }
  if (!link) return;

  // open wallet
  walletTools.showWalletApp();
  // redirect route
  const searchParams = new URLSearchParams();
  searchParams.set('url', link);
  await walletRouter.navigate(`/main/send?${searchParams.toString()}`);
}

async function handleDeepLinkHaloAuthWebQrLogin() {
  return;
}

const DEEP_LINK_HALO_CHAT_DETAIL = '/h/d';
const DEEP_LINK_HALO_WALLET_RECEIVE = '/w/r';
const DEEP_LINK_HALO_AUTH_WEB_QR_Login = '/a/ql';

const DEEP_LINKS = [
  DEEP_LINK_HALO_CHAT_DETAIL,
  DEEP_LINK_HALO_WALLET_RECEIVE,
  DEEP_LINK_HALO_AUTH_WEB_QR_Login,
];

export function useOpenLink() {
  const navigate = useNavigate();
  const client = useAppStore((state) => state.client);
  const fetchProfileGroup = useActionsStore((state) => state.fetchProfileGroup);
  const fetchProfileUser = useActionsStore((state) => state.fetchProfileUser);

  const handlers = useMemo<HandlerReturn[]>(
    () => [
      // Inner link
      {
        test: (url) => {
          if (url.origin !== window.location.origin) return false;
          // check it is in deep links
          return !DEEP_LINKS.some((deepLink) => url.pathname.startsWith(deepLink));
        },
        handle: (url) => {
          // Only navigate if this URL can be matched by our app (and is not just the 404 route)
          const matches = matchRoutes(router.routes, {
            pathname: url.pathname,
            search: url.search,
            hash: url.hash,
          });

          if (!matches || matches.length === 0) {
            return;
          }

          const hasNonNotFoundRoute = matches.some(
            (m) => m.route.path && m.route.path !== Paths.NOT_FOUND
          );

          if (!hasNonNotFoundRoute) {
            return;
          }

          navigate(url.pathname + url.search + url.hash);
        },
      },
      // Deep link
      {
        test: (url) => {
          if (url.origin !== window.location.origin) return false;
          // check it is in deep links
          return DEEP_LINKS.some((deepLink) => url.pathname.startsWith(deepLink));
        },
        handle: async (url) => {
          if (url.pathname.startsWith(Paths.BASE + DEEP_LINK_HALO_AUTH_WEB_QR_Login)) {
            await handleDeepLinkHaloAuthWebQrLogin();
            return;
          }
          if (url.pathname.startsWith(Paths.BASE + DEEP_LINK_HALO_WALLET_RECEIVE)) {
            await handleDeepLinkHaloWalletReceive({
              client,
              url,
            });
            return;
          }
          if (url.pathname.startsWith(Paths.BASE + DEEP_LINK_HALO_CHAT_DETAIL)) {
            await handleDeepLinkHaloChatDetail({
              client,
              fetchProfileUser,
              fetchProfileGroup,
              url,
            });
            return;
          }
          navigate(url.pathname + url.search + url.hash);
        },
      },
      // Landing link
      {
        test: (url) => {
          return url.host === import.meta.env.VITE_LANDING_HOST;
        },
        handle: async (url) => {
          if (url.pathname.startsWith(DEEP_LINK_HALO_AUTH_WEB_QR_Login)) {
            await handleDeepLinkHaloAuthWebQrLogin();
            return;
          }
          if (url.pathname.startsWith(DEEP_LINK_HALO_WALLET_RECEIVE)) {
            await handleDeepLinkHaloWalletReceive({
              client,
              url,
            });
            return;
          }
          if (url.pathname.startsWith(DEEP_LINK_HALO_CHAT_DETAIL)) {
            await handleDeepLinkHaloChatDetail({
              client,
              fetchProfileUser,
              fetchProfileGroup,
              url,
            });
            return;
          }
          // last match
          await handleDeepLinkHaloChatDetail({
            client,
            fetchProfileUser,
            fetchProfileGroup,
            url,
          });
        },
      },
      // other link
      { test: () => true, handle: (url) => window.open(url.toString(), '_blank') },
    ],
    [navigate, client, fetchProfileUser, fetchProfileGroup]
  );

  const handler = useCallback(
    async (url: string) => {
      const _ = new URL(url);
      for (let i = 0; i < handlers.length; i++) {
        const { test, handle } = handlers[i];
        if (!test(_)) continue;
        const res = await handle(_);
        if (res === false) continue;
        return true;
      }
      return false;
    },
    [handlers]
  );

  return { openLink: handler };
}
